/**
 * Toast Store
 * Управление toast-уведомлениями для важных ошибок и событий
 */

import { create } from 'zustand';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message: string;
  duration?: number; // В миллисекундах, по умолчанию 5000 для error, 3000 для остальных
  jobId?: string; // ID задачи, если toast связан с задачей
  onClick?: () => void; // Callback при клике на toast
}

interface ToastState {
  toasts: Toast[];
  showToast: (toastOrMessage: Omit<Toast, 'id'> | string, type?: ToastType) => void;
  removeToast: (id: string) => void;
  clearAll: () => void;
}

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],

  showToast: (toastOrMessage: Omit<Toast, 'id'> | string, type?: ToastType) => {
    // Поддержка как объекта, так и упрощенного синтаксиса (message, type)
    let toast: Omit<Toast, 'id'>;
    
    if (typeof toastOrMessage === 'string') {
      // Упрощенный синтаксис: showToast('message', 'type')
      toast = {
        type: type || 'info',
        title: type === 'error' ? 'Ошибка' : type === 'success' ? 'Успешно' : type === 'warning' ? 'Внимание' : 'Информация',
        message: toastOrMessage,
      };
    } else {
      // Полный объект
      toast = toastOrMessage;
    }

    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const duration = toast.duration || (toast.type === 'error' ? 10000 : 5000);
    
    const newToast: Toast = {
      ...toast,
      id,
      duration,
    };

    set((state) => ({
      toasts: [...state.toasts, newToast],
    }));

    // Автоматическое удаление через duration
    if (duration > 0) {
      setTimeout(() => {
        get().removeToast(id);
      }, duration);
    }
  },

  removeToast: (id: string) => {
    set((state) => ({
      toasts: state.toasts.filter((toast) => toast.id !== id),
    }));
  },

  clearAll: () => {
    set({ toasts: [] });
  },
}));

/**
 * Helper функция для показа уведомления о запуске задачи
 */
export function showJobStartedToast(store: ReturnType<typeof useToastStore.getState>, jobId: string, jobName: string) {
  store.showToast({
    type: 'info',
    title: 'Задача запущена',
    message: `${jobName}. Нажмите, чтобы посмотреть прогресс.`,
    jobId: jobId,
    duration: 8000,
  });
}

